import { useRef, useState } from "react";
import styled from "styled-components";
import { FaRegCalendarAlt } from "react-icons/fa";
import { HiOutlineChevronRight } from "react-icons/hi";
import MaskedInput from "react-text-mask";
import Calendar from "react-calendar";
import { formatBrazil } from "~/utils/date";
import OutsideAlerter from "~/shared/OutsideAlerter";

const InputContainer = styled.div`
    .inner-input {
        background: #fff;
        border-radius: 8px;
        padding: 1px 5px 1px 5px;
        position: relative;
        height: 40px;
        border: 1px solid #B7B7B7;
        margin-bottom: 10px;
    }

    @media (max-width: 1200px) {
        .inner-input {
            border-radius: 8px;
            width: 100%;
        }
    }

    &.expanded .inner-input {
        border-radius: 8px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    label {
        font-size: 16px;
        font-weight: bold;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        margin-bottom: 10px;
        display: block;
    }

    input {
        padding: 0 5px;
        width: calc(50% - 20px);
        height: 100%;
        background: transparent;
    }

    .separator {
        position: relative;
        top: 3px;
    }

    button {
        background: none;
        border: none;
        font-size: 16px;
        position: relative;
        top: 3px;
    }

    .expanded-area {
        position: absolute;
        z-index: 2;
        left: 0;
    }

    .expanded-area .react-calendar {
        border-top-left-radius: 0;
    }

    @media (max-width: 1200px) {
        .expanded-area {
            width: 100%;
        }
        .expanded-area .react-calendar {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
        }
    }
`;

type DateIntervalInputProps = {
    name: string;
    onChange: (name: string, value: [string, string]) => void;
    value?: [string, string];
    label?: string;
};

const DateIntervalInput = ({ name, onChange, label = "", value = ["", ""] }: DateIntervalInputProps) => {
    const [startValue, endValue] = value;
    const [expanded, setExpanded] = useState(false);

    const startRef = useRef(null);
    const endRef = useRef(null);

    const calendarChange = (e: [Date, Date]) => {
        const [initial, final] = e;

        onChange(name, [formatBrazil(initial), formatBrazil(final)]);
        setExpanded(false);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name: dataName, value: dataValue } = e.target;

        const data: [string, string] = dataName.includes("start")
            ? [dataValue, value?.[1] ?? ""]
            : [value?.[0] ?? "", dataValue];

        onChange(name, data);
    };

    const clickOut = () => {
        setExpanded(false);
    };

    const toggleExpandedArea = () => {
        setExpanded(!expanded);
    };

    return (
        <InputContainer className={`${expanded ? "expanded" : ""}`}>
            {label ? <label htmlFor={`${name}_start`}>{label}</label> : ""}

            <div className="inner-input">
                <MaskedInput
                    id={`${name}_start`}
                    name={`${name}_start`}
                    mask={[/[0-3]/, /[0-9]/, '/', /[0-1]/, /[0-9]/, '/', /\d/, /\d/, /\d/, /\d/]}
                    value={startValue}
                    ref={startRef}
                    placeholder="Data inicial"
                    onChange={handleChange}
                />
                <HiOutlineChevronRight className="separator" />

                <MaskedInput
                    name={`${name}_end`}
                    mask={[/[0-3]/, /[0-9]/, '/', /[0-1]/, /[0-9]/, '/', /\d/, /\d/, /\d/, /\d/]}
                    value={endValue}
                    ref={endRef}
                    placeholder="Data final"
                    onChange={handleChange}
                />

                <button type="button" onClick={toggleExpandedArea}>
                    <FaRegCalendarAlt />
                </button>

                {expanded ? (
                    <OutsideAlerter className="expanded-area" onOutside={clickOut}>
                        <Calendar selectRange onChange={calendarChange} />
                    </OutsideAlerter>
                ) : (
                    ""
                )}
            </div>
        </InputContainer>
    );
};

export default DateIntervalInput;
